import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// components:
import CheckoutLayout from "core/CheckoutLayout";
import CheckoutForm from "core/CheckoutForm";
import {
    CheckoutProvider,
    useUpdateBillingInfo
} from "frontend/context/CheckoutContext";

import { GeneralFormErrorMessage } from "icons/IconCheckout/styles";

// mutations/queries:
import { GET_SAVED_CARD } from "./queries";
import { ADD_PURCHASED_PHOTO } from "./mutations";

// helpers
import { PHOTO_DETAIL } from "data/links";
import useFeatureFlag from "frontend/hooks/useFeatureFlag";
import { useQuery, useMutation } from "@apollo/client";
import {
    CardNumberElement,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import { getFormattedData } from "photos/helpers";
import { useCheckoutFormGeneralError } from "frontend/context/CheckoutContext";
import {
    TRACK_ITEM_PHOTO,
    TRACK_PURCHASE_ALC,
    trackViewCheckout,
    trackPurchase
} from "core/trackEvent";
import { PREMIUM_PHOTOS_FLAG } from "frontend/data/featureFlags";

const UnwrappedPhotoCheckout = ({ photo, selectedLicense }) => {
    const {
        id: photoId,
        slugWithId,
        assets: { thumbnailMedium: thumbnail },
        title,
        creator
    } = photo;

    const {
        receiptInfo,
        submitBillingInfo,
        setReceiptInfo
    } = useUpdateBillingInfo();

    const hasPremiumPricing = useFeatureFlag(PREMIUM_PHOTOS_FLAG);

    // query for saved card data:
    const { data: { savedCard: response = {} } = {} } = useQuery(
        GET_SAVED_CARD,
        {
            notifyOnNetworkStatusChange: true
        }
    );

    useEffect(() => {
        if (response && response.receiptInfo) {
            setReceiptInfo(response.receiptInfo);
        }
    }, [response]);

    // helper functions to process data
    // used throughout CheckoutForm
    const {
        initialForm,
        photoDetailPagePath,
        hasSavedCard,
        licenseSchemas,
        selectedPrice
    } = getFormattedData({
        slugWithId,
        response,
        selectedLicense,
        photo,
        photoId,
        hasPremiumPricing
    });

    const trackingData = {
        itemType: TRACK_ITEM_PHOTO,
        itemId: photoId,
        itemName: title,
        purchaseType: TRACK_PURCHASE_ALC,
        price: selectedPrice
    };
    useEffect(() => {
        trackViewCheckout(trackingData);
    }, []);

    // set up/assign const states:
    const [saveCard, setSaveCard] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [purchased, setPurchased] = useState(false);
    const [loading, setLoading] = useState(false);

    const stripe = useStripe();
    const elements = useElements();
    const {
        generalFormError,
        setGeneralFormError
    } = useCheckoutFormGeneralError();

    // submit purchase photo functions:
    const handlePurchaseComplete = ({ purchasePhoto }) => {
        if (purchasePhoto.ok) {
            trackPurchase(trackingData);

            setPurchased(true);
            setGeneralFormError("");
            // add photo purchase type to redirect URL after checkout - ?purchase parameter is for analytics:
            const photoPurchaseType = `?purchase=${selectedLicense.toLowerCase()}`;
            const photoDetailUrl = encodeURI(
                PHOTO_DETAIL.replace(":photoSlugWithId", slugWithId) +
                    photoPurchaseType
            );

            // Don't show the checkout form again if the user clicks "back"
            window.location.replace(photoDetailUrl);
        } else if (purchasePhoto.errors) {
            setGeneralFormError(purchasePhoto.errors);
            setLoading(false);
        }
    };

    const [purchasePhoto] = useMutation(ADD_PURCHASED_PHOTO, {
        onCompleted: handlePurchaseComplete
    });

    const submitFormWithNewCardInfo = async e => {
        e.preventDefault();

        if (!form.name) {
            return setGeneralFormError("Please fill out all required fields");
        }

        setLoading(true);

        const cardNumberElement = elements.getElement(CardNumberElement);
        const { token, error } = await stripe.createToken(
            cardNumberElement,
            form
        );

        if (error) {
            setGeneralFormError(error.message);
            setLoading(false);
        } else {
            purchasePhoto({
                variables: {
                    input: {
                        purchaseType: selectedLicense.toUpperCase(),
                        photoId,
                        newCard: {
                            token: token.id,
                            name: form.name,
                            country: "U.S.",
                            receiptInfo,
                            saveCard
                        }
                    }
                }
            });
        }
    };

    const submitFormWithSavedCardInfo = async e => {
        e.preventDefault();

        setLoading(true);

        const isSuccess = await submitBillingInfo();

        if (isSuccess) {
            purchasePhoto({
                variables: {
                    input: {
                        purchaseType: selectedLicense.toUpperCase(),
                        photoId,
                        savedCard: { cardId: response.id }
                    }
                }
            });
        }
    };

    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setGeneralFormError("");
    };

    const saveCardCheckboxOnClick = () => {
        setSaveCard(!saveCard);
    };

    return (
        <CheckoutLayout
            pageHeading="Purchase Photo"
            thumbnail={thumbnail}
            imageTitle={title}
            slugWithId={slugWithId}
            creator={creator}
        >
            {generalFormError && (
                <GeneralFormErrorMessage
                    handleDismissClick={() => setGeneralFormError("")}
                >
                    {generalFormError}
                </GeneralFormErrorMessage>
            )}
            <CheckoutForm
                cancelLink={photoDetailPagePath}
                form={form}
                handleFormChange={handleFormChange}
                hasSavedCard={hasSavedCard}
                isPhotoCheckout={true}
                isSubmitDisabled={purchased}
                itemPrice={selectedPrice}
                licenseSchemas={licenseSchemas}
                loading={loading}
                response={response}
                saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                saveCardInitialValue={saveCard}
                showOrderSummary={false}
                slugWithId={slugWithId}
                submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                submitFormWithSavedCardInfo={submitFormWithSavedCardInfo}
                thumbnail={thumbnail}
                title={title}
            />
        </CheckoutLayout>
    );
};

const PhotoCheckout = props => (
    <CheckoutProvider>
        <UnwrappedPhotoCheckout {...props} />
    </CheckoutProvider>
);

PhotoCheckout.propTypes = {
    selectedLicense: PropTypes.string.isRequired,
    photo: PropTypes.object.isRequired
};

export default PhotoCheckout;
